import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';

import Button from '@/components/Button/Button';
import Menu from '@/components/Menu/Menu';
import type { AggregatedReportListItem } from '@/entities/report';
import {
  deleteReportMutationOptions,
  reportDetailQueryOptions,
} from '@/entities/report';
import {
  deleteStudioItemMutationOptions,
  studioItemQueryOptions,
} from '@/entities/studio';
import { usePopup } from '@/stores/pure/PopupStore';
import useUserStore from '@/stores/UserStore';

export const REPORT_DELETE_OWNER_TOOLTIP =
  'You can only delete reports you created.';

type Props = {
  report: AggregatedReportListItem;
  onRename: () => void;
  onShare: () => void;
};

export function ReportActionMenu({ report, onRename, onShare }: Props) {
  const queryClient = useQueryClient();
  const setPopup = usePopup();
  const currentUserId = useUserStore((state) => state.user?.user_id);
  const reportDetailQuery = useQuery(
    reportDetailQueryOptions(
      { report_id: report.alias },
      { enabled: false },
    ),
  );
  const studioItemQuery = useQuery(
    studioItemQueryOptions(
      { item_id: report.alias },
      { enabled: false },
    ),
  );
  const deleteReportMutation = useMutation(
    deleteReportMutationOptions(queryClient),
  );
  const deleteStudioItemMutation = useMutation(
    deleteStudioItemMutationOptions(queryClient),
  );
  const creatorId =
    report.source_type === 'HEY_SYNCLY'
      ? studioItemQuery.data?.created_by
      : reportDetailQuery.data?.created_by;
  const canDelete = Boolean(currentUserId && creatorId === currentUserId);

  const loadCreator = () => {
    if (report.source_type === 'HEY_SYNCLY') {
      void studioItemQuery.refetch();
      return;
    }

    void reportDetailQuery.refetch();
  };

  const confirmDelete = () => {
    if (!canDelete) return;

    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Delete report',
      message: 'Are you sure you want to delete this report?',
      onConfirm: () => {
        void (async () => {
          if (report.source_type === 'HEY_SYNCLY') {
            await deleteStudioItemMutation.mutateAsync({
              item_id: report.alias,
            });
          } else {
            await deleteReportMutation.mutateAsync({
              report_id: report.alias,
            });
          }
        })();
      },
    });
  };

  return (
    <div
      className="flex w-full justify-end"
      onClick={(event) => event.stopPropagation()}
    >
      <Menu
        onAnchorClickAction={loadCreator}
        triggerNode={
          <Button
            variant="ghost_secondary"
            sizeType="s"
            aria-label={`Open actions for ${report.title}`}
            leftIcon={<MoreHorizontal />}
          />
        }
        options={[
          { label: 'Rename', onClick: onRename },
          { label: 'Share', onClick: onShare },
          {
            label: 'Delete',
            disabled: !canDelete,
            tooltip: canDelete ? undefined : REPORT_DELETE_OWNER_TOOLTIP,
            onClick: confirmDelete,
          },
        ]}
      />
    </div>
  );
}
